import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, ArrowLeft, Car, CalendarClock, User, PartyPopper, Calendar as CalendarIcon, AlertTriangle, Mail, Phone, MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CarteGriseScanner } from "@/components/CarteGriseScanner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useClientAuth } from "@/context/ClientAuthContext";

const TIME_SLOTS = ["08:30", "09:30", "10:30", "11:30", "13:30", "14:30", "15:30", "16:30"];
const TRACK_ONLY = ["electronic-auto"];
const CAL_CLS = {
  caption_label: "font-display font-700 text-white uppercase tracking-wide text-sm",
  nav_button: "h-7 w-7 inline-flex items-center justify-center bg-transparent border border-white/15 text-white hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors rounded-none",
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  head_cell: "text-[#00E5FF] font-mono-tech text-[10px] uppercase w-9 font-normal",
  cell: "relative p-0 text-center",
  day: "h-9 w-9 font-mono-tech text-sm text-zinc-300 hover:bg-white/10 rounded-none transition-colors aria-selected:opacity-100",
  day_selected: "bg-[#E60000] text-white hover:bg-[#E60000] focus:bg-[#E60000]",
  day_today: "border border-[#00E5FF]/50 text-[#00E5FF]",
  day_outside: "text-zinc-700 opacity-40",
  day_disabled: "text-zinc-700 line-through opacity-40",
  day_hidden: "invisible",
};

export default function Booking() {
  const { t, lang } = useLang();
  const { user } = useClientAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [vehicle, setVehicle] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [slotInfo, setSlotInfo] = useState(null); // {closed, slots:[{time,available}]}
  const [dateOpen, setDateOpen] = useState(false);
  const [form, setForm] = useState({
    service_id: location.state?.service || "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });

  // Booking is reserved to logged-in clients with a complete profile.
  useEffect(() => {
    if (user && !user.profile_complete) navigate("/account", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (location.state?.vehicle) {
      const v = location.state.vehicle;
      const cleaned = Object.fromEntries(Object.entries(v).filter(([k, val]) => k !== "id" && val != null && String(val).trim() !== ""));
      if (Object.keys(cleaned).length) setVehicle(cleaned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.preferred_date) { setSlotInfo(null); return; }
    api.get(`/availability?date=${form.preferred_date}`).then(({ data }) => {
      setSlotInfo(data);
      const stillOk = !data.closed && data.slots.some((s) => s.time === form.preferred_time && s.available);
      if (!stillOk && form.preferred_time) set("preferred_time", "");
    }).catch(() => setSlotInfo(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.preferred_date]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVeh = (k, v) => setVehicle((prev) => ({ ...(prev || {}), [k]: v }));
  const hasVehicle = vehicle && Object.values(vehicle).some((v) => v != null && String(v).trim() !== "");
  const cleanVehicle = () => {
    if (!hasVehicle) return null;
    const out = {};
    Object.entries(vehicle).forEach(([k, v]) => { if (v != null && String(v).trim() !== "") out[k] = String(v).trim(); });
    return out;
  };
  const today = new Date().toISOString().split("T")[0];
  const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const fmtDate = (s) => new Date(s + "T00:00:00").toLocaleDateString(lang === "en" ? "en-GB" : lang === "nl" ? "nl-BE" : "fr-BE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const sLabel = (id) => { const k = `services.${id}.label`; const v = t(k); return v === k ? (services.find((s) => s.id === id)?.label || "—") : v; };
  const sDesc = (s) => { const k = `services.${s.id}.desc`; const v = t(k); return v === k ? (s.desc || "") : v; };

  const canSubmit = form.service_id && form.preferred_date && form.preferred_time && user?.name && user?.email && user?.phone;

  const submit = async () => {
    if (!canSubmit) { toast.error(t("booking.toast_required")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", {
        ...form,
        name: user.name,
        email: user.email,
        phone: user.phone,
        vehicle: cleanVehicle(),
        lang,
      });
      setConfirmed(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { n: 1, label: t("booking.step_vehicle"), icon: Car },
    { n: 2, label: t("booking.step_service"), icon: CalendarClock },
    { n: 3, label: t("booking.step_contact"), icon: User },
  ];

  if (confirmed) {
    return (
      <div className="noise-overlay bg-[#050505] min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 pt-40 pb-32 text-center" data-testid="booking-confirmation">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="inline-flex w-20 h-20 items-center justify-center border border-[#00E5FF]/40 bg-[#00E5FF]/5 mb-8">
            <PartyPopper className="w-9 h-9 text-[#00E5FF]" />
          </motion.div>
          <h1 className="font-display font-900 text-white text-4xl sm:text-5xl tracking-tighter uppercase">{t("booking.conf_title_quote")}</h1>
          <p className="text-zinc-400 mt-4 leading-relaxed">
            {t("booking.conf_desc", { name: confirmed.name, service: sLabel(confirmed.service_id), date: confirmed.preferred_date, time: confirmed.preferred_time })}
          </p>
          <p className="text-[#00E5FF] mt-3 text-sm leading-relaxed max-w-lg mx-auto">{t("booking.quote_info")}</p>
          <div className="mt-10 border border-white/10 bg-[#0C0C0E] p-6 text-left font-mono-tech text-sm">
            <div className="flex justify-between py-2 border-b border-white/5"><span className="text-zinc-600">{t("booking.conf_service")}</span><span className="text-white">{sLabel(confirmed.service_id)}</span></div>
            <div className="flex justify-between py-2 border-b border-white/5"><span className="text-zinc-600">{t("booking.conf_date")}</span><span className="text-white">{confirmed.preferred_date}</span></div>
            <div className="flex justify-between py-2 border-b border-white/5"><span className="text-zinc-600">{t("booking.conf_time")}</span><span className="text-white">{confirmed.preferred_time}</span></div>
            <div className="flex justify-between py-2"><span className="text-zinc-600">{t("booking.conf_status")}</span><span className="text-amber-400">{t("status.pending")}</span></div>
          </div>
          {confirmed.quote_token && (
            <a href={`${window.location.origin}/devis/${confirmed.quote_token}`} data-testid="booking-track-link" className="inline-flex items-center justify-center gap-2 mt-8 px-7 py-3.5 bg-[#00E5FF] text-black font-display font-700 uppercase tracking-wide hover:bg-[#33eaff] transition-colors">
              {t("booking.track")}
            </a>
          )}
          <div>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 text-zinc-500 hover:text-white text-sm font-mono-tech uppercase tracking-wide transition-colors" data-testid="confirmation-home-btn">
              {t("booking.conf_home")}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-32 pb-24">
        <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#00E5FF]">{t("booking.overline")}</span>
        <h1 className="font-display font-900 text-white text-4xl sm:text-5xl tracking-tighter uppercase mt-3">{t("booking.title")}</h1>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-10 mb-12">
          {steps.map((s, i) => (
            <div key={s.n} className="flex items-center flex-1 last:flex-none">
              <div className={`flex items-center gap-3 ${step >= s.n ? "opacity-100" : "opacity-40"}`}>
                <div className={`w-10 h-10 flex items-center justify-center border ${step >= s.n ? "border-[#E60000] bg-[#E60000]/10 text-[#E60000]" : "border-white/20 text-zinc-500"}`}>
                  <s.icon className="w-4 h-4" />
                </div>
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-400 hidden sm:block">{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-px mx-3 ${step > s.n ? "bg-[#E60000]" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="fade-up">
            <p className="text-zinc-400 mb-6 leading-relaxed">{t("booking.step1_desc")}</p>
            <CarteGriseScanner onDetected={setVehicle} />

            <div className="flex items-center gap-4 my-8">
              <div className="flex-1 h-px bg-white/10" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600">{t("booking.or_manual")}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <h2 className="font-display font-700 text-white text-xl tracking-tight mb-5 flex items-center gap-2"><Car className="w-5 h-5 text-[#E60000]" /> {t("booking.manual_title")}</h2>
            <div className="grid sm:grid-cols-2 gap-5" data-testid="booking-manual-vehicle">
              {[
                { k: "marque", label: t("booking.v_marque"), ph: t("booking.v_marque_ph") },
                { k: "modele", label: t("booking.v_modele"), ph: t("booking.v_modele_ph") },
                { k: "annee", label: t("booking.v_annee"), ph: t("booking.v_annee_ph") },
                { k: "immatriculation", label: t("booking.v_immat"), ph: t("booking.v_immat_ph") },
                { k: "vin", label: t("booking.v_vin"), ph: t("booking.v_vin_ph") },
                { k: "cylindree", label: t("booking.v_cylindree"), ph: t("booking.v_cylindree_ph") },
              ].map((f) => (
                <div key={f.k}>
                  <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{f.label}</label>
                  <Input value={vehicle?.[f.k] || ""} onChange={(e) => setVeh(f.k, e.target.value)} data-testid={`booking-veh-${f.k}`} placeholder={f.ph} className="bg-[#0C0C0E] border-white/10 rounded-none h-12 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-10">
              <span />
              <button onClick={() => { if (!hasVehicle) { toast.error(t("booking.veh_required")); return; } setStep(2); }} data-testid="booking-next-1" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors">
                {t("booking.continue")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="fade-up">
            <h2 className="font-display font-700 text-white text-xl tracking-tight mb-5">{t("booking.choose_service")}</h2>
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 mb-8">
              {services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => set("service_id", s.id)}
                  data-testid={`booking-service-${s.id}`}
                  className={`text-left p-5 transition-colors ${form.service_id === s.id ? "bg-[#E60000]/10 border-l-2 border-[#E60000]" : "bg-[#0C0C0E] hover:bg-[#121215]"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display font-700 text-white">{sLabel(s.id)}</span>
                    {form.service_id === s.id && <CheckCircle2 className="w-4 h-4 text-[#E60000]" />}
                  </div>
                  <p className="text-zinc-500 text-xs mt-1.5 leading-relaxed">{sDesc(s)}</p>
                  {s.price && <span className="inline-block mt-2 font-mono-tech text-xs text-[#00E5FF] tracking-wider" data-testid={`booking-service-price-${s.id}`}>{s.price}</span>}
                </button>
              ))}
            </div>

            {TRACK_ONLY.includes(form.service_id) && (
              <div data-testid="track-only-disclaimer" className="flex items-start gap-3 border border-amber-400/30 bg-amber-400/5 px-4 py-3 mb-8">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-amber-300/90 text-[11px] leading-relaxed">{t("booking.track_only")}</p>
              </div>
            )}

            <h2 className="font-display font-700 text-white text-xl tracking-tight mb-4">{t("booking.datetime")}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("booking.date")}</label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date"
                      className="w-full h-12 flex items-center gap-3 px-4 bg-[#0C0C0E] border border-white/10 text-left hover:border-[#00E5FF]/50 data-[state=open]:border-[#00E5FF] transition-colors"
                    >
                      <CalendarIcon className="w-4 h-4 text-[#00E5FF] shrink-0" />
                      <span className={`font-mono-tech text-sm capitalize truncate ${form.preferred_date ? "text-white" : "text-zinc-600"}`}>
                        {form.preferred_date ? fmtDate(form.preferred_date) : t("booking.date_ph")}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-2 bg-[#0C0C0E] border border-[#00E5FF]/30 shadow-[0_0_25px_rgba(0,229,255,0.14)]">
                    <Calendar
                      mode="single"
                      selected={form.preferred_date ? new Date(form.preferred_date + "T00:00:00") : undefined}
                      onSelect={(d) => { if (d) { set("preferred_date", toIso(d)); setDateOpen(false); } }}
                      disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                      weekStartsOn={1}
                      initialFocus
                      classNames={CAL_CLS}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("booking.slot")}</label>
                {!form.preferred_date ? (
                  <div className="border border-white/10 bg-[#0C0C0E] py-6 text-center font-mono-tech text-[11px] uppercase tracking-[0.12em] text-zinc-600">—</div>
                ) : !slotInfo ? (
                  <div className="border border-white/10 bg-[#0C0C0E] py-6 text-center font-mono-tech text-[11px] uppercase tracking-[0.12em] text-zinc-600">{t("booking.loading_slots")}</div>
                ) : slotInfo.closed ? (
                  <div className="border border-[#FF1A1A]/30 bg-[#FF1A1A]/5 py-6 text-center font-mono-tech text-[11px] uppercase tracking-[0.12em] text-[#FF1A1A]" data-testid="booking-closed">{t("booking.closed_day")}</div>
                ) : slotInfo.slots.length === 0 ? (
                  <div className="border border-white/10 bg-[#0C0C0E] py-6 text-center font-mono-tech text-[11px] uppercase tracking-[0.12em] text-zinc-600">{t("booking.no_slots")}</div>
                ) : (
                  <div className="grid grid-cols-4 gap-px bg-white/10">
                    {slotInfo.slots.map((s) => (
                      <button
                        key={s.time}
                        disabled={!s.available}
                        onClick={() => set("preferred_time", s.time)}
                        data-testid={`booking-time-${s.time}`}
                        className={`py-3 font-mono-tech text-xs transition-colors ${
                          !s.available ? "bg-[#0C0C0E] text-zinc-700 line-through cursor-not-allowed"
                          : form.preferred_time === s.time ? "bg-[#E60000] text-white"
                          : "bg-[#0C0C0E] text-zinc-400 hover:bg-[#121215]"
                        }`}
                      >
                        {s.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between mt-10">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white font-display font-700 uppercase tracking-wide hover:border-white/50 transition-colors" data-testid="booking-back-2">
                <ArrowLeft className="w-4 h-4" /> {t("booking.back")}
              </button>
              <button
                onClick={() => { if (!form.service_id || !form.preferred_date || !form.preferred_time) { toast.error(t("booking.toast_step2")); return; } setStep(3); }}
                data-testid="booking-next-2"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"
              >
                {t("booking.continue")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="fade-up">
            <h2 className="font-display font-700 text-white text-xl tracking-tight mb-2">{t("booking.account_contact")}</h2>
            <p className="text-zinc-500 text-sm mb-5">{t("booking.account_hint")}</p>
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-6" data-testid="booking-account-info">
              <div className="bg-[#0C0C0E] p-4 flex items-center gap-3">
                <User className="w-4 h-4 text-[#00E5FF] shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-zinc-600">{t("booking.name")}</div>
                  <div className="text-white text-sm truncate" data-testid="booking-account-name">{user?.name}</div>
                </div>
              </div>
              <div className="bg-[#0C0C0E] p-4 flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#00E5FF] shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-zinc-600">{t("booking.phone")}</div>
                  <div className="text-white text-sm truncate" data-testid="booking-account-phone">{user?.phone}</div>
                </div>
              </div>
              <div className="bg-[#0C0C0E] p-4 flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#00E5FF] shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-zinc-600">{t("booking.email")}</div>
                  <div className="text-white text-sm truncate" data-testid="booking-account-email">{user?.email}</div>
                </div>
              </div>
              <div className="bg-[#0C0C0E] p-4 flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[#00E5FF] shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-zinc-600">{t("booking.address")}</div>
                  <div className="text-white text-sm truncate" data-testid="booking-account-address">{user?.address}</div>
                </div>
              </div>
            </div>
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("booking.message")}</label>
              <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="booking-message" placeholder={t("booking.message_ph")} className="bg-[#0C0C0E] border-white/10 rounded-none text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[110px]" />
            </div>

            {/* Recap */}
            <div className="mt-8 border border-white/10 bg-[#0C0C0E] p-5 font-mono-tech text-sm">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#00E5FF] mb-3">{t("booking.recap")}</div>
              <div className="flex justify-between py-1.5"><span className="text-zinc-600">{t("booking.r_service")}</span><span className="text-white">{sLabel(form.service_id)}</span></div>
              <div className="flex justify-between py-1.5"><span className="text-zinc-600">{t("booking.r_date")}</span><span className="text-white">{form.preferred_date || "—"} {form.preferred_time}</span></div>
              {vehicle?.marque && <div className="flex justify-between py-1.5"><span className="text-zinc-600">{t("booking.r_vehicle")}</span><span className="text-white">{vehicle.marque} {vehicle.modele} · {vehicle.immatriculation}</span></div>}
            </div>

            <div className="flex justify-between mt-10">
              <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white font-display font-700 uppercase tracking-wide hover:border-white/50 transition-colors" data-testid="booking-back-3">
                <ArrowLeft className="w-4 h-4" /> {t("booking.back")}
              </button>
              <button onClick={submit} disabled={submitting} data-testid="booking-submit" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
                {submitting ? t("booking.sending") : t("booking.confirm")} <CheckCircle2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
